import { AxiosError } from "axios";
import api from "../../services/caronte";
import { Redirect } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Button from "../../rxlib/components/buttons/button";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import ButtonLink from "../../rxlib/components/buttons/button-link";
import { obterIdProduto, obterVersao } from "../../services/utilitarios";
import { ModalWarning } from "../../rxlib/components/modal/modal-warning";
import { obterLicenciada, tokenExpirado } from "../../rxlib/services/seguranca";
import { AcessoProps, ApiError, FormLogin, LoginProduto } from "../../services/tipos";
import { criptografar, obterAmbiente, tratarErroApi } from "../../rxlib/services/utilitarios";

function Login(props: AcessoProps) {
    const [acao, setAcao] = useState(props.match.params.acao);
    const [carregando, setCarregando] = useState<boolean>(false);
    const [showWarning, setShowWarning] = useState<boolean>(false);
    const [messageWarning, setMessageWarning] = useState<string[]>([]);
    const [token, setToken] = useState<string>(useAppSelector(state => state.token));
    const [logado, setLogado] = useState<boolean>(useAppSelector(state => state.logado));
    const [tempoRestanteBloqueio, setTempoRestanteBloqueio] = useState<number | null>(null);
    const [expirado, setExpirado] = useState<boolean>(useAppSelector(state => state.expirado));

    const { register, handleSubmit } = useForm<FormLogin>();

    const dispatch = useAppDispatch();
    const tentativasDeLogin: number = useAppSelector(state => state.tentativasDeLogin);
    const ultimaTentativaDeLogin: Date | undefined = useAppSelector(state => state.ultimaTentativaDeLogin);

    const handleHide = () => setShowWarning(false);

    const obterSegundosQuePassouBloqueio = useCallback(() => {
        if (!ultimaTentativaDeLogin)
            return 0;

        return ((Date.now() - new Date(ultimaTentativaDeLogin).getTime()) / 1000);
    }, [ultimaTentativaDeLogin]);

    const obterTempoRestanteBloqueio = useCallback(() => {
        const segundosEmUmMinuto: number = 60;
        return segundosEmUmMinuto - obterSegundosQuePassouBloqueio();
    }, [obterSegundosQuePassouBloqueio]);

    const loginEstaBloqueado = useCallback(() => {
        const quantidadeTentativasParaBloqueio: number = 3;
        if (tentativasDeLogin >= quantidadeTentativasParaBloqueio) {
            const segundosDesdeUltimoBloqueio: number = obterSegundosQuePassouBloqueio();

            if (segundosDesdeUltimoBloqueio >= 60) {
                dispatch({ type: "RESET_LOGIN_ATTEMPTS" });
                return false;
            }

            return true;
        }
        return false;
    }, [tentativasDeLogin, obterSegundosQuePassouBloqueio, dispatch]);

    const obterTextoAguardeDesbloqueio = useCallback(() => {
        if (tempoRestanteBloqueio != null) {
            return `Aguarde ${tempoRestanteBloqueio} ${tempoRestanteBloqueio > 1 ? " segundos" : " segundo"}`;
        }

        return "";
    }, [tempoRestanteBloqueio]);

    const showLoginBloqueado = useCallback(() => {
        const tempo = Math.round(obterTempoRestanteBloqueio());
        setTempoRestanteBloqueio(tempo);
        setMessageWarning(["O acesso está bloqueado por falhas na tentativa de login", `Aguarde ${tempo} segundos`]);
        setShowWarning(true);
        setAcao("");
    }, [obterTempoRestanteBloqueio]);

    useEffect(() => {
        if (loginEstaBloqueado())
            showLoginBloqueado();
    }, [loginEstaBloqueado, showLoginBloqueado]);

    const onSubmit: SubmitHandler<FormLogin> = data => {
        if (loginEstaBloqueado()) {
            showLoginBloqueado();
            return;
        }

        setCarregando(true);
        let login: LoginProduto = {
            email: data.email,
            idProduto: obterIdProduto(),
            senha: criptografar(data.senha),
        }

        api.post("/Login/Authenticate", login)
            .then(response => {
                setTimeout(() => {
                    dispatch({
                        type: "LOG_IN",
                        token: response.data.token,
                        tenantId: obterLicenciada(response.data.token),
                    });
                    setToken(response.data.token);
                    setLogado(true);
                }, 250);
            }).catch((error: AxiosError<ApiError>) => {
                if (error.response?.status === 400) {
                    dispatch({ type: "LOG_IN_FAILED" });
                }
                tratarErro(error);
            });
    }

    function tratarErro(error: AxiosError<ApiError>) {
        setCarregando(false);
        setMessageWarning(tratarErroApi(error, "Não foi possível realizar o login: "));
        setShowWarning(true);
    }

    function showSecaoExpirada() {
        setMessageWarning(["A seção do usuário expirou.", "Realize o login novamente."]);
        setShowWarning(true);
        setExpirado(false);
    }

    function showAcessoBloqueado() {
        setMessageWarning(["O acesso foi bloqueado pois o usuário não está logado."]);
        setShowWarning(true);
        setAcao("");
    }

    useEffect(() => {
        if (!tempoRestanteBloqueio) {
            return;
        }

        const interval = setInterval(() => {
            setTempoRestanteBloqueio(prev => {
                if (prev !== null) {
                    const novo = prev - 1;
                    if (novo <= 0) {
                        setShowWarning(false);
                        return null;
                    }
                    setMessageWarning(["O acesso está bloqueado por falhas na tentativa de login",
                        `Aguarde ${novo} segundos`]);
                    return novo;
                }
                return null;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [tempoRestanteBloqueio]);

    return (
        <>
            {
                !tokenExpirado(token)
                    ? logado
                        ? <Redirect to="/home" />
                        : ""
                    : ""
            }
            {
                expirado
                    ? showSecaoExpirada()
                    : ""
            }
            {
                acao === "acessobloqueado"
                    ? showAcessoBloqueado()
                    : ""
            }
            <div className="login-content d-flex align-items-center">
                <form className="form-signin mx-auto rxlib-login" onSubmit={handleSubmit(onSubmit)}>
                    <div className="text-center mb-3">
                        <label className="rxlib-nome">CargasSac</label>
                    </div>
                    <div className="form-label-group">
                        <input ref={register({ required: true })} name="email" type="email" id="inputEmail" className="form-control" placeholder="Endereço de e-mail" required autoFocus />
                        <label htmlFor="inputEmail">Endereço de e-mail</label>
                    </div>
                    <div className="form-label-group">
                        <input ref={register({ required: true })} name="senha" type="password" id="inputPassword" className="form-control" placeholder="Senha" required />
                        <label htmlFor="inputPassword">Senha</label>
                    </div>
                    <div className={!carregando ? "d-flex align-middle" : "d-grid align-middle"}>
                        {
                            !carregando
                                ? <ButtonLink
                                    link="\"
                                    texto="Voltar"
                                    classStyle="btn-rxlib"
                                    className="btn-lg btn-voltar" />
                                : ""
                        }
                        <Button
                            type="submit"
                            texto="Entrar"
                            classStyle="btn-rxlib"
                            carregando={carregando}
                            className="btn-lg btn-logar-pesquisar"
                            disabled={tempoRestanteBloqueio !== null}
                            classNameDivSpinner="login-spinner-button" />
                    </div>
                    {
                        tempoRestanteBloqueio !== null
                            ? <p className="mt-4 text-muted text-center">
                                {
                                    obterTextoAguardeDesbloqueio()
                                }
                            </p>
                            : ""
                    }
                    <p className="mt-4 mb-0 text-muted text-center">
                        © 2025 {obterVersao()}
                        <span className="rxlib-homologacao">
                            {obterAmbiente()}
                        </span>
                    </p>
                </form>
            </div>
            <ModalWarning
                show={showWarning}
                onHide={handleHide}
                message={messageWarning} />
        </>
    );
}

export default Login;